import { Component, Input, SimpleChanges } from '@angular/core';
import { RequestServiceService } from '../../request-service.service';
import { HireSearchService } from '../../services/hire-search.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FilterStoreService } from '../../store/filter-store.service';
import { ActivatedRoute } from '@angular/router';
import { HireSkeletonLoadersComponent } from '../../hire-skeleton-loaders/hire-skeleton-loaders.component';
import { TitleCasePipe } from '@angular/common';
import { RoleFitDataService } from '../../services/role-file-data.service';
import { MatTooltipModule } from '@angular/material/tooltip';

interface RoleFitRow {
  id: string;
  criteria: string;
  reasoning: string;
  details?: string;
  // The raw, un-summarized criteria text actually sent to the explanation API.
  // Used for the chip tooltip instead of the AI-generated reasoning.
  content?: string;
  score: number | null;
  icon: SafeHtml;
}
@Component({
  selector: 'app-hire-slider-ai-insights-view',
  imports: [HireSkeletonLoadersComponent, TitleCasePipe, MatTooltipModule],
  templateUrl: './hire-slider-ai-insights-view.component.html',
  styleUrl: './hire-slider-ai-insights-view.component.scss',
})
export class HireSliderAiInsightsViewComponent {
  @Input() candidate: any;
  roleFitTableData: RoleFitRow[] = [];
  viewMode: 'table' | 'detail' = 'table'; // default view
  criteriaCount: number = 0;
  jdFitInsightsData: any[] = [];
  searchType: string = 'search';
  showExplanationLoader: boolean = false;
  showJDFitInsightsLoader: boolean = false;
  @Input() calledfromData: string = 'Search'; // default value
  showLongSummaryLoader: boolean = false;
  similarityInsightsData: any[] = [];
  showSimilarityInsightsLoader: boolean = false;
  // Keyed (c1, c2, ...) map of the raw criteria text actually sent to the explanation
  // API (passed down from hire-slider), used to show the original criteria name on
  // the chip tooltip instead of the AI-summarized reasoning.
  @Input() rawScreeningCriteriaText: Record<string, string> = {};
  showEmptyState: boolean = false;

  constructor(private requestService: RequestServiceService, private hireService: HireSearchService, private sanitizer: DomSanitizer, private filterStore: FilterStoreService, private route: ActivatedRoute, private roleFitDataService: RoleFitDataService
  ) {

  }

  ngOnInit() {
    this.criteriaCount =  this.filterStore.getFilters()?.screeningCriteria?.filter((c: string) => c?.trim())?.length;
    this.jdFitInsightsData = this.candidate.jobFitment;
    this.searchType = this.route.snapshot.queryParams['type'] || 'search';

  }

  getSimilarCandidateInsights() {
    const aggReq = this.requestService.getHireRequestWithFilters();
    const payload = {
      referenceelasticid: aggReq?.exclude_persons?.[0] || null,
      tocompareelasticid: this.candidate?.id,
    }

    this.hireService.candidateCompare(payload).subscribe((res: any) => {
      if (res.status === 'success') {
        console.log('AI Insights:', res.data);
      } else {
        console.error('Error fetching AI insights:', res);
      }
    });
  }

  /**
 * Method to format AI Summary Text
 * @param text
 */
  formatAISummary(text: string) {
    let cleanedText = text?.replace(/<\/?em>/g, '');

    if (!cleanedText?.includes('BULLET:-')) {
      return cleanedText?.trim();
    }

    const bulletItems = cleanedText?.split('BULLET:-')
      .map(item => item?.trim())
      .filter(item => item.length > 0);

    const listItems = bulletItems.map(item => `<li>${item}</li>`).join('');
    return `<ul>${listItems}</ul>`;

  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['candidate']) {
      if(this.calledfromData === 'Project' || this.calledfromData === 'pipeline-list' || this.calledfromData === 'project-upload-candidates' || this.calledfromData === 'project-inbound-applications') {
        this.buildCriteriaTableDataForProject();
        this.jdFitInsightsData = this.candidate.jobFitment;
      this.similarityInsightsData = this.candidate.similarityExplanation;

      } else {
      this.buildRoleFitTableData();
      this.jdFitInsightsData = this.candidate.jobFitment;
      this.similarityInsightsData = this.candidate.similarityExplanation;
      }

    }
  }

  /**
   * Builds table/detailed view rows by joining:
   * - screeningCriteriaSummary (label)
   * - overallExplanation.screening_criteria (reasoning, matched on lowercase "c1")
   * - personSearchScore.screening_criteria (score, matched on uppercase "C1")
   */
  buildRoleFitTableData() {
    this.showExplanationLoader = this.candidate?.showExplanation ?? false;
    this.showJDFitInsightsLoader = this.candidate?.jdFitLoader ?? false;
    this.showLongSummaryLoader = this.candidate?.showLongSummaryLoader ?? false;
    this.showSimilarityInsightsLoader = this.candidate?.showSimilarityInsightsLoader ?? false;
    this.showEmptyState = this.candidate?.showEmptyState ?? false;
    const summary = this.candidate?.screeningCriteriaSummary;
    if (!summary || typeof summary !== 'object') {
      this.roleFitTableData = [];
      return;
    }
    const explanationList = this.candidate?.overallExplanation?.screening_criteria ?? [];
    const scoreList = this.candidate?.personSearchScore?.screening_criteria ?? [];

    this.roleFitTableData = Object.keys(summary).map((key) => {
      const label = summary[key];

      const explanationObj = explanationList.find(
        (e: any) => e?.criteria?.toLowerCase() === key.toLowerCase()
      );
      const scoreObj = scoreList.find(
        (s: any) => s?.criteria?.toLowerCase() === key.toLowerCase()
      );
      const score = scoreObj?.score ?? null;
      const { short, full } = this.parseBulletExplanation(explanationObj?.explanation);

      return {
        id: key,
        criteria: label,
        reasoning: short,
        details: full,
        // Prefer the raw text actually sent to the explanation API over the
        // AI-summarized label, so the tooltip shows the original criteria text.
        content: this.rawScreeningCriteriaText?.[key] ?? label,
        score,
        icon: this.sanitizer.bypassSecurityTrustHtml(this.getScoreIconSvg(score)),
      };
    });

    this.roleFitDataService.setRoleFitData(
      this.candidate?.id,
      Object.keys(summary).map((key) => {
        const scoreObj = scoreList.find((s: any) => s?.criteria?.toLowerCase() === key.toLowerCase());
        return {
          criteria: key,
          criteria_content: summary[key],
          score: scoreObj?.score ?? null,
        };
      })
    );
  }

  /**
  * Splits "BULLET:-text1.BULLET:-text2" style explanation strings into:
  * - short: first bullet only (used in table view reasoning column)
  * - full: all bullets joined (used in detailed view)
  */
  private parseBulletExplanation(text?: string): { short: string; full: string } {
    if (!text) {
      return { short: '', full: '' };
    }

    if (!text.includes('BULLET:-')) {
      return { short: text.trim(), full: text.trim() };
    }

    const bullets = text
      .split('BULLET:-')
      .map((item) => item.replace(/\.$/, '').trim())
      .filter((item) => item.length > 0);

    return {
      short: bullets[0] ?? '',
      full: bullets.join(' '),
    };
  }

  /**
   * Returns the correct status icon SVG string based on score thresholds:
   * >=9 green check | 5-9 orange warning | <5 red cross | null neutral (no score)
   */
  private getScoreIconSvg(score: number | null): string {
    if(score >= 8) {
return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.75 9.75L4.25 12.25M7.75 8.25L10.25 5.75M5.75 9.75L8.25 12.25L14.25 5.75" stroke="#39B141" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
    }
    else if (score < 8 && score >= 6) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2.75 8.75L6.25 12.25L13.25 4.75" stroke="#68AA86" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>`;
    } else if (score < 6 && score >= 4) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 8H12H4Z" fill="#FBA744" />
              <path d="M4 8H12" stroke="#FBA744" stroke-width="2" stroke-linecap="round" />
            </svg>`;
    } else {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M11.25 4.75L4.75 11.25M4.75 4.75L11.25 11.25" stroke="#E57777" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>`;
    }
  }

  setViewMode(mode: 'table' | 'detail') {
    this.viewMode = mode;
  }

  buildCriteriaTableDataForProject() {
    this.showExplanationLoader = this.candidate?.showExplanation ?? false;
    this.showJDFitInsightsLoader = this.candidate?.jdFitLoader ?? false;

    this.roleFitTableData = (this.candidate?.projectExplanation ?? [])?.map((item: any) => {
      const score = item.score ?? null;
      const { short, full } = this.parseBulletExplanation(item.explanation);

      return {
        id: item.criteria,
        criteria: item.summarizedcriteriachip ? item.summarizedcriteriachip : this.getFirstNWords(item.criteria_content, 2),
        reasoning: short,
        details: full,
        // Full, un-summarized criteria text (as sent to the project explanation API),
        // used for the chip tooltip instead of the AI reasoning.
        content: item.criteria_content,
        score,
        icon: this.sanitizer.bypassSecurityTrustHtml(this.getScoreIconSvg(score)),
      };
    });

    this.roleFitDataService.setRoleFitData(
      this.candidate?.id,
      (this.candidate?.projectExplanation ?? []).map((item: any) => ({
        criteria: item.criteria,
        criteria_content: item.criteria_content,
        score: item.score ?? null,
      }))
    );
  }

  private getFirstNWords(text: string, n: number): string {
  if (!text) return '';
  const words = text.trim().split(/\s+/);
  const truncated = words.slice(0, n).join(' ');
  return words.length > n ? `${truncated}...` : truncated;
}

getTitlecase(str: string){
  return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}

}
